import '@babylonjs/loaders/glTF';
import {
    AbstractMesh,
    AnimationPropertiesOverride,
    SceneLoader,
    TransformNode,
    type AssetContainer,
    type AnimationGroup,
    type Scene,
    type Skeleton,
} from '@babylonjs/core';
import { normalizeVector2, type Vector2, type WorldSpace } from '@sorrem/shared';
import { worldSpaceToScenePosition } from '../world-to-scene-position';
import { listMeshesUnder } from '../mesh-tree';
import { TILE_UNIT_SIZE } from '../scale-constants';

interface AnimationBlendState {
    currentAnimName: string;
}

const animationBlendStateMap = new WeakMap<Skeleton | AbstractMesh, AnimationBlendState>();

/**
 * Each character's own blend settings, keyed like `animationBlendStateMap`
 * (or by the group, for a character with no meshes).
 *
 * Babylon reads blending from a joint's `animationPropertiesOverride` ahead
 * of the clip's own `Animation` (`RuntimeAnimation` takes `enableBlending`
 * as a run starts and `blendingSpeed` every frame). Retargeted clones share
 * their `Animation` tracks across every character of a skeleton type (see
 * `cloneRetargetedAnimationGroups`), so setting blending on the group, which
 * writes through to those tracks, changed it for every character at once:
 * one character's cut made another's blend snap. The joints are each
 * character's own, so an override on them keeps the blend per character.
 */
const blendOverrides = new WeakMap<object, AnimationPropertiesOverride>();

/** Where a cut lands in its clip: the first frame, or the last (a one-shot already played out). */
export type AnimationCut = 'start' | 'end';

/**
 * Sets how `character` enters `group` before it starts: a blend over
 * `blendDurationMs`, or a cut. Puts the character's override on every joint
 * the group drives, so a clip that reaches joints others do not still uses it.
 */
function applyCharacterBlend(stateKey: object | undefined, group: AnimationGroup, blendDurationMs: number, cut: boolean): void {
    const key = stateKey ?? group;
    let override = blendOverrides.get(key);
    if (!override) {
        override = new AnimationPropertiesOverride();
        blendOverrides.set(key, override);
    }
    override.enableBlending = !cut;
    override.blendingSpeed = blendDurationMsToBlendingSpeed(blendDurationMs);
    for (const targeted of group.targetedAnimations) {
        const target = targeted.target as { animationPropertiesOverride: AnimationPropertiesOverride | null } | null;
        if (target && target.animationPropertiesOverride !== override) {
            target.animationPropertiesOverride = override;
        }
    }
}

/**
 * Render frames per second assumed when converting a blend *duration* into
 * Babylon's per-frame blend *speed*.
 *
 * Babylon's blending is frame-counted, not time-based: `RuntimeAnimation`
 * advances `_blendingFactor += blendingSpeed` exactly once per evaluation (one
 * per rendered frame), lerping from the pose captured when the blend began
 * until the factor reaches 1. A blend therefore takes `1 / blendingSpeed`
 * rendered frames, whatever real time that turns out to be - on a machine
 * rendering at 30fps every blend takes twice as long in wall-clock terms.
 * Expressing the intent in ms and converting here keeps the call sites
 * readable; it does not make the blend time-accurate.
 */
const ASSUMED_RENDER_FPS = 60;

/** Floor on blending speed, so a very long duration can't stall a blend near-permanently. */
const MIN_BLENDING_SPEED = 0.001;

/**
 * Converts a desired blend duration in ms into Babylon's `blendingSpeed` - the
 * per-frame increment described on `ASSUMED_RENDER_FPS`.
 *
 * A non-positive duration yields `1`. `playCharacterAnimation` treats that
 * duration as a cut and turns blending off instead: a blend starts at factor
 * 0, so even at speed 1 its first frame is still the old pose.
 */
export function blendDurationMsToBlendingSpeed(blendDurationMs: number): number {
    if (!Number.isFinite(blendDurationMs) || blendDurationMs <= 0) {
        return 1;
    }
    const blendFrames = (blendDurationMs / 1000) * ASSUMED_RENDER_FPS;
    return Math.min(1, Math.max(MIN_BLENDING_SPEED, 1 / blendFrames));
}

/**
 * Real playback duration of `group` at 1x speed, derived from its actual
 * keyframe range rather than any guessed constant. Each clip is read at its
 * own frame rate: the glTF loader keys clips at 60fps whatever they were
 * authored at, and assuming 30 here reported every clip at twice its length,
 * so anything sized to a window (a swing, the dodge roll) finished halfway
 * through it.
 */
export function resolveAnimationGroupDurationMs(group: Pick<AnimationGroup, 'targetedAnimations'>): number {
    let durationMs = 0;
    for (const targetedAnimation of group.targetedAnimations) {
        const { animation } = targetedAnimation;
        const keys = animation.getKeys();
        const lastKey = keys[keys.length - 1];
        if (lastKey && animation.framePerSecond > 0) {
            durationMs = Math.max(durationMs, (lastKey.frame / animation.framePerSecond) * 1000);
        }
    }
    return durationMs;
}

export interface LoadedCharacterMesh {
    /** The glTF import's root transform node - move/rotate this to move the whole character. */
    readonly root: TransformNode;
    readonly meshes: readonly AbstractMesh[];
    readonly skeletons: readonly Skeleton[];
    readonly animationGroups: readonly AnimationGroup[];
    /**
     * Every TransformNode the import produced, including the skin joint
     * hierarchy. glTF skeletal animations target these joint nodes directly
     * (a `Skeleton`'s `Bone`s are a derived view - a `Bone`'s pose comes
     * from its linked TransformNode, not the reverse), so anything that
     * needs to retarget an AnimationGroup onto this character (see
     * `retargetAnimationsToCharacter` in `character-animation-base.ts`)
     * needs this list, not `skeletons[].bones`.
     */
    readonly transformNodes: readonly TransformNode[];
}

/**
 * Loads a character glTF/GLB (e.g. one produced by
 * `@sorrem/character-pipeline`) into `scene`. `rootUrl` + `fileName` follow
 * Babylon's own `SceneLoader.ImportMeshAsync` split (e.g. `('/assets/models/barbarian/', 'barbarian.glb')`
 * for the server's static `/assets` route - see `assets-router.ts`).
 *
 * Does not position, scale, or play anything - see `updateCharacterTransform`
 * and `playCharacterAnimation`. Caller owns the loaded result's lifetime
 * (dispose `meshes`/`skeletons`/`animationGroups` when done with it).
 */
export async function loadCharacterMesh(scene: Scene, rootUrl: string, fileName: string): Promise<LoadedCharacterMesh> {
    const result = await SceneLoader.ImportMeshAsync('', rootUrl, fileName, scene);
    const root = result.meshes[0];
    if (!root) {
        throw new Error(`loadCharacterMesh: ${rootUrl}${fileName} imported zero meshes.`);
    }
    return {
        root,
        meshes: result.meshes,
        skeletons: result.skeletons,
        animationGroups: result.animationGroups,
        transformNodes: result.transformNodes,
    };
}

/**
 * An independent copy of the character model in `container`: its own
 * transform hierarchy, skeleton and animation groups, named as the source's
 * so animations retarget onto it by joint name. How the game makes every
 * character (`CharacterAssetCache.instantiateCharacter`), and how the model
 * pipeline's pose-metrics bake makes the one it measures. `label` names the
 * model in errors.
 */
export function instantiateCharacterMesh(container: AssetContainer, label: string): LoadedCharacterMesh {
    // Identity nameFunction (not `undefined`): retargetAnimationsToCharacter
    // (character-animation-base.ts) matches the shared animation base's
    // joints onto this character's own joints by exact node name (e.g.
    // "mixamorig:Hips") - prefixing every cloned node's name with the
    // character model id broke every single one of those lookups, silently
    // (retargeting leaves a non-matching targeted animation pointed at the
    // now-detached base joint rather than erroring). Passing `undefined`
    // here doesn't avoid that class of bug the way it looks like it should:
    // Babylon's instantiateModelsToScene unconditionally clones every
    // non-instanceable node (which includes every skeleton joint) as
    // `"Clone of " + node.name` internally, and only overrides that back to
    // the original name via nameFunction(source.name) - it does NOT fall
    // back to the source name when nameFunction is falsy. So `undefined`
    // silently left every joint named "Clone of mixamorig:Hips" etc.,
    // failing every retarget lookup and leaving every character's skeleton
    // frozen at its raw bind pose (T-pose) - visible but never animated.
    const instantiated = container.instantiateModelsToScene((name) => name, false);

    const root = instantiated.rootNodes[0];
    if (!root) {
        throw new Error(`instantiateCharacterMesh: ${label} produced zero root nodes`);
    }

    const meshes = listMeshesUnder(root);

    const transformNodes = root.getDescendants(
        false,
        (node): node is TransformNode => node instanceof TransformNode,
    );
    if (root instanceof TransformNode) {
        transformNodes.unshift(root);
    }

    return {
        root: root as TransformNode,
        meshes,
        skeletons: instantiated.skeletons,
        animationGroups: instantiated.animationGroups,
        transformNodes,
    };
}

/**
 * Positions, scales, and rotates a loaded character's root to face `facing`, using
 * the exact same `worldSpaceToScenePosition` ground/wall meshes use - so a
 * character standing on a given tile lines up with the ground mesh built
 * for that same tile.
 *
 * Scaling: characters are scaled to match the tile unit size (default 1 unit = 1 tile).
 * This ensures characters are appropriately sized relative to the game world.
 *
 * Rotation: `facing` is a normalized `{x, y}` direction vector in the same
 * world-space axes `worldSpaceToScenePosition` uses for positions, so a
 * movement direction and a position derived from the same world-space data
 * stay consistent. The yaw is `atan2(facing.y, facing.x)` with no additional
 * offset - re-verify (via `debug:babylon-character`) rather than assume
 * this holds for any other model's rest-pose orientation.
 */
export function updateCharacterTransform(
    root: TransformNode,
    worldPosition: WorldSpace,
    facing: Vector2,
    tileUnitSize: number = TILE_UNIT_SIZE,
    levelHeight: number = 0,
    scale: number = 1,
): void {
    const scenePosition = worldSpaceToScenePosition(worldPosition, tileUnitSize, levelHeight);
    root.position.set(scenePosition.x, scenePosition.y, scenePosition.z);
    // The glTF loader converts right-handed glTF into this left-handed scene
    // with a 180-degree turn and a (1, 1, -1) scaling on the import's
    // __root__: together, a mirror on local X. Facing replaces the turn, so
    // keep the mirror as (-1, 1, 1), which leaves the forward axis alone.
    // A uniform positive scale here drew every character mirrored (a
    // right-hand weapon in the left hand).
    const handedness = Math.sign(root.scaling.x * root.scaling.y * root.scaling.z) < 0 ? -1 : 1;
    root.scaling.set(scale * handedness, scale, scale);
    updateCharacterFacing(root, facing);
}

/**
 * Rotates a loaded character's root to face `facing`, without touching
 * position or scale - the piece of `updateCharacterTransform` that applies
 * on its own wherever a character isn't placed on the tile grid (e.g. an
 * animation-preview harness that only cares about facing).
 *
 * `facing` need not be pre-normalized - this normalizes it itself (falling
 * back to the previous rotation for a zero/invalid vector) so any caller
 * passing a raw direction doesn't need to remember to do so first.
 */
export function updateCharacterFacing(root: TransformNode, facing: Vector2): void {
    // glTF-imported root nodes commonly have rotationQuaternion set (non-null)
    // by the loader; when it's set, Babylon's transform system uses it
    // instead of .rotation, so writes to .rotation.y are silently ignored - 
    // a real bug on the first attempt here, caught only by the visual check
    // (unit tests used a bare `new TransformNode(...)`, which has
    // rotationQuaternion === null by default, so they never exercised this).
    // Clearing it makes Euler .rotation the source of truth going forward.
    root.rotationQuaternion = null;
    const directionUnit = normalizeVector2(facing);
    if (!directionUnit) {
        return;
    }
    // Note: Character model rotation to match facing direction.
    // The model's rest pose and the world coordinate system require careful alignment.
    // Adjust if the character is facing the wrong direction (backwards, rotated 90 degrees, etc).
    // Standard mapping: atan2(y, x) where y is vertical (north-south) and x is horizontal (east-west).
    root.rotation.y = Math.atan2(directionUnit.y, directionUnit.x);
}

/**
 * Plays `name` if the loaded character has an animation group with that exact
 * name, blending into it from whatever pose the character is currently in.
 *
 * Blending: the outgoing group is stopped, which leaves the joints frozen at
 * its last pose, and the incoming group is started with blending enabled on
 * the character's own joints (see `blendOverrides`), so Babylon lerps each
 * joint from that frozen pose into the new clip over `blendDurationMs` (see
 * `blendDurationMsToBlendingSpeed` for how a duration becomes Babylon's
 * per-frame blending speed, and why it is frames rather than wall-clock
 * time). Babylon does *not* blend AnimationGroup transitions on its own:
 * without it every transition here is a single-frame pose snap.
 *
 * If already playing `name`, only updates `speedRatio` and leaves the
 * animation playing. Returns whether an animation group actually matched - the
 * caller decides what a miss means (fall back to another name, do nothing, log
 * it once). This does not attempt to normalize/guess names: whether a given
 * asset's exported animation groups actually correspond to Sorrem's
 * `idle`/`walk`/`attack`/`death` vocabulary must be verified per asset, not
 * assumed (see the barbarian's two unverified group names in its
 * ATTRIBUTION.md).
 *
 * Optional `speedRatio` controls playback speed (default 1.0). Use values < 1
 * to slow down, > 1 to speed up. For walk animations, adjust this to match
 * the character's world movement speed.
 *
 * Blending works identically for Quaternius and Mixamo animation sources
 * because it leverages Babylon.js's skeleton animation system, which is
 * animation-source-agnostic.
 *
 * `restart` plays it again from the top even if it is already playing (a
 * combo's next swing of the same clip).
 *
 * A `blendDurationMs` of 0 or less, or a `cut`, enters the clip with no
 * blend at all, posed at once (before any frame is drawn): at its first
 * frame, or with `cut` `'end'`, at its last. A cut always starts the clip
 * over, even if it is playing. It is how a character takes its start pose
 * as it appears, instead of blending out of whatever pose its joints were
 * left in (see `Character.poseAction`).
 */
export function playCharacterAnimation(
    character: Pick<LoadedCharacterMesh, 'animationGroups' | 'meshes'>,
    name: string,
    loop: boolean = true,
    speedRatio: number = 1.0,
    blendDurationMs: number = 300,
    restart = false,
    cut?: AnimationCut,
): boolean {
    const group = character.animationGroups.find((candidate) => candidate.name === name);

    // Use the first mesh as the state key (all meshes share the same skeletons)
    const stateKey = character.meshes[0];
    const blendState = stateKey
        ? (animationBlendStateMap.get(stateKey) || { currentAnimName: '' })
        : { currentAnimName: '' };

    // If not found, stop all and return false
    if (!group) {
        for (const other of character.animationGroups) {
            if (other.isPlaying) {
                other.stop();
            }
        }
        if (stateKey) {
            animationBlendStateMap.set(stateKey, { currentAnimName: '' });
        }
        return false;
    }

    // If already playing this animation, just update speed; asked to restart, it starts over,
    // blending from the pose it was stopped in.
    const isCut = cut !== undefined || !(blendDurationMs > 0);
    if ((restart || cut !== undefined) && group.isPlaying) {
        group.stop();
    }
    if (blendState.currentAnimName === name && group.isPlaying) {
        group.speedRatio = speedRatio;
        return true;
    }

    // Stopping the outgoing group leaves its joints at their last evaluated
    // pose, which is exactly the pose the incoming group's blend interpolates
    // away from - so a hard stop here is correct, not a gap, once the incoming
    // group blends.
    const isNewAnimation = blendState.currentAnimName !== name;
    for (const other of character.animationGroups) {
        if (other !== group && other.isPlaying) {
            other.stop();
        }
    }

    // Start the new animation if needed
    group.speedRatio = speedRatio;
    if (!group.isPlaying) {
        // Set before start(): `enableBlending` is copied onto each
        // RuntimeAnimation as the group starts, so a later assignment would
        // not apply to the run that just began.
        applyCharacterBlend(stateKey, group, blendDurationMs, isCut);
        // start() takes its own speed (default 1) and overwrites the one
        // just set, so a clip asked to start fast began at 1x.
        group.start(loop, speedRatio);
        if (isCut) {
            // Posed now rather than on the next frame's evaluation.
            group.goToFrame(cut === 'end' ? group.to : group.from);
        }
    }

    // Update state only if this was a real animation change
    if (isNewAnimation && stateKey) {
        blendState.currentAnimName = name;
        animationBlendStateMap.set(stateKey, blendState);
    }

    return true;
}
